import { Injectable } from "@angular/core";
import { BehaviorSubject, of } from "rxjs";
import { tap } from "rxjs/operators";
import { Product } from "src/app/model/price/product";
import { BuyRequest } from "src/app/model/shop/buy-request";
import { PriceService } from "../price/price.service";

@Injectable({
  providedIn: "root",
})
export class CartService {
  PRICE_TIMEOUT = 15 * 60 * 1000 + 100;
  second = 1000;
  minute = this.second * 60;
  hour = this.minute * 60;
  defaultTimeText = "15:00";
  cartTimer = new BehaviorSubject<string>(this.defaultTimeText);
  cartTimeout = new BehaviorSubject<boolean>(false);
  intervalTimer;

  constructor(private priceService: PriceService) {}

  cartList = new BehaviorSubject<any[]>(null);
  cartListTotalPrice = new BehaviorSubject<number>(0);
  buyRequest = new BehaviorSubject<BuyRequest>(null);

  refreshBuyRequest(data) {
    this.buyRequest.next({ ...this.buyRequest.value, ...data });
  }

  submitBasketElements() {
    const basketElements = this.cartList.getValue().map((product: any) => {
      return {
        productId: product.id,
        productName: product.name,
        quantity: product.quantity,
      };
    });
    this.refreshBuyRequest({ basketElements: basketElements });
  }

  existInCart(productId: string) {
    return (
      this.cartList.value &&
      this.cartList.value.some((item) => item.id == productId)
    );
  }

  addToCart(product: Product) {
    let currentList;
    if (this.cartList.getValue() && this.cartList.getValue().length > 0) {
      currentList = [...this.cartList.getValue().concat(product)];
    } else {
      currentList = [product];

      const current = new Date().getTime();
      const countdownDate = new Date(current + this.PRICE_TIMEOUT).getTime();
      this.putCountdownDateIntoLocalStorage(countdownDate);
      this.startCartTimer(countdownDate);
    }
    this.putCartListIntoLocalStorage(currentList);
    this.cartList.next(currentList);
    this.syncCartListTotalPrice();
  }

  syncCartListTotalPrice() {
    const totalPrice = this.cartList.value?.reduce(
      (acc, val) => acc + val.buy * val.quantity,
      0
    );
    this.cartListTotalPrice.next(totalPrice);
  }

  startCartTimer(countdownDate) {
    this.cartTimeout.next(false);

    this.intervalTimer = setInterval(() => {
      const now = new Date().getTime();
      const distance = countdownDate - now;

      if (distance > 0) {
        const minute = Math.floor((distance % this.hour) / this.minute);
        const second = Math.floor((distance % this.minute) / this.second);
        this.cartTimer.next(
          `${minute < 10 ? "0" + minute : minute}:${
            second < 10 ? "0" + second : second
          }`
        );
      } else {
        this.cartTimer.next(this.defaultTimeText);
        this.cartTimeout.next(true);
        clearInterval(this.intervalTimer);
      }
    }, 1000);
  }

  putCartListIntoLocalStorage(cartlist: Product[]) {
    localStorage.setItem("cart", JSON.stringify(cartlist));
  }

  putCountdownDateIntoLocalStorage(countdownDate) {
    localStorage.setItem("cartCountdownDate", JSON.stringify(countdownDate));
  }

  getCartListFromLocalStorage(): Product[] {
    return JSON.parse(localStorage.getItem("cart"));
  }

  syncCart() {
    const cartList: Product[] = JSON.parse(localStorage.getItem("cart"));
    this.cartList.next(cartList);
    this.syncCartListTotalPrice();
    const countdownDate = JSON.parse(localStorage.getItem("cartCountdownDate"));
    if (cartList && countdownDate) {
      this.startCartTimer(countdownDate);
    }
    return of(true);
  }

  removeAllProductFromCart() {
    localStorage.removeItem("cart");
    this.clearTimer();
    this.cartList.next(null);
    this.syncCartListTotalPrice();
  }

  removeFromCart(product: Product) {
    let currentList = this.cartList.value;
    const index = currentList.map((product) => product.id).indexOf(product.id);
    if (index >= 0) {
      currentList.splice(index, 1);
    }
    this.putCartListIntoLocalStorage(currentList);
    this.cartList.next(currentList);
    if (currentList.length == 0) {
      localStorage.removeItem("cart");
      this.clearTimer();
    }
    this.syncCartListTotalPrice();
  }

  clearTimer() {
    this.cartTimer.next(this.defaultTimeText);
    this.cartTimeout.next(false);
    localStorage.removeItem("cartCountdownDate");
    clearInterval(this.intervalTimer);
  }

  updateProductPrice(product) {
    return this.priceService.syncProductPrice(product).pipe(
      tap((syncedProduct) => {
        syncedProduct.quantity = product.quantity;
        this.addToCart(syncedProduct);
      })
    );
  }

  updateProductQuantity(product: Product, quantity: number) {
    const currentProductList = this.cartList.value;
    const currentProduct = currentProductList.find(
      (mProduct: Product) => mProduct.id == product.id
    );
    currentProduct.quantity = quantity;
    this.putCartListIntoLocalStorage(currentProductList);
    this.cartList.next(currentProductList);
    this.syncCartListTotalPrice();
  }
}
